/**
 * The brands a card merchant name is recognised by, for showing their mark. The card network names a merchant
 * as each terminal or gateway sends it ("FACEBK *ADS 1234", "META ADS", "FB *PAY"), so a name is upper-cased,
 * trimmed and its spaces collapsed, then matched at its start as a whole word ("METALURGICA" is not Meta).
 *
 * Mirrors coloca-nest `src/coloca/metrics/utils/merchant-brands.ts`, which groups the staff merchant ranking
 * by the same brands and answers with their `name`; that name is recognised here too. Add a brand to both.
 */
export interface MerchantBrand {
  name: string;
  pattern: RegExp;
  /** The brand's own colour; `null` for brands whose mark is black or white, drawn in the text colour. */
  color: string | null;
}

export const MERCHANT_BRANDS: readonly MerchantBrand[] = [
  { name: "Meta", pattern: /^(FACEBK|FACEBOOK|META|INSTAGRAM|WHATSAPP)\b|^FB ?\*/, color: "#0866FF" },
  { name: "Google", pattern: /^(GOOGLE|YOUTUBE|GSUITE|G SUITE)\b/, color: "#4285F4" },
  { name: "Amazon", pattern: /^(AMAZON|AMZN|AWS|PRIME VIDEO|AUDIBLE)\b/, color: "#FF9900" },
  { name: "Apple", pattern: /^(APPLE|ITUNES)\b/, color: null },
  { name: "Microsoft", pattern: /^(MICROSOFT|MSFT|XBOX)\b/, color: "#00A4EF" },
  { name: "LinkedIn", pattern: /^LINKEDIN\b/, color: "#0A66C2" },
  { name: "OpenAI", pattern: /^(OPENAI|CHATGPT)\b/, color: null },
  { name: "Anthropic", pattern: /^(ANTHROPIC|CLAUDE\.AI)\b/, color: "#D97757" },
  { name: "Uber", pattern: /^UBER\b/, color: null },
  { name: "DiDi", pattern: /^DIDI\b/, color: "#FF7E33" },
  { name: "Rappi", pattern: /^RAPPI\b/, color: "#FF441F" },
  { name: "Netflix", pattern: /^NETFLIX\b/, color: "#E50914" },
  { name: "Spotify", pattern: /^SPOTIFY\b/, color: "#1DB954" },
  { name: "TikTok", pattern: /^TIK ?TOK\b/, color: null },
  { name: "X", pattern: /^(TWITTER|X CORP)\b|^X$/, color: null },
  { name: "Airbnb", pattern: /^AIRBNB\b/, color: "#FF5A5F" },
  { name: "Booking.com", pattern: /^BOOKING\b/, color: "#003580" },
  { name: "Mercado Libre", pattern: /^(MERCADO ?LIBRE|MERCADO ?PAGO|MELI)\b/, color: "#2D3277" },
  { name: "Canva", pattern: /^CANVA\b/, color: "#00C4CC" },
  { name: "Adobe", pattern: /^ADOBE\b/, color: "#FA0F00" },
  { name: "Shopify", pattern: /^SHOPIFY\b/, color: "#5E8E3E" },
  { name: "Zoom", pattern: /^ZOOM\b/, color: "#0B5CFF" },
  { name: "Notion", pattern: /^NOTION\b/, color: null },
  { name: "Avianca", pattern: /^AVIANCA\b/, color: "#DA291C" },
  { name: "LATAM Airlines", pattern: /^LATAM\b/, color: "#ED1650" },
  { name: "PayPal", pattern: /^PAYPAL\b/, color: "#003087" },
  { name: "Alibaba", pattern: /^(ALIBABA|ALIEXPRESS|ALIPAY)\b/, color: "#FF6A00" },
  { name: "Temu", pattern: /^TEMU\b/, color: "#FB7701" },
  { name: "Shein", pattern: /^SHEIN\b/, color: null },
  { name: "Shopee", pattern: /^SHOPEE\b/, color: "#EE4D2D" },
  { name: "Etsy", pattern: /^ETSY\b/, color: "#F1641E" },
  { name: "Slack", pattern: /^SLACK\b/, color: "#4A154B" },
  { name: "Discord", pattern: /^DISCORD\b/, color: "#5865F2" },
  { name: "GitHub", pattern: /^GITHUB\b/, color: null },
  { name: "Figma", pattern: /^FIGMA\b/, color: "#F24E1E" },
  { name: "Steam", pattern: /^(STEAM|STEAMPOWERED)\b/, color: null },
];

/** A merchant name as the matching reads it: upper-cased, trimmed, spaces collapsed. */
export function normalizeMerchantName(name: string | null | undefined): string {
  return (name ?? "").trim().toUpperCase().replace(/\s+/g, " ");
}

/** The brand a merchant name belongs to, or null when it is not a known one. */
export function merchantBrandOf(name: string | null | undefined): MerchantBrand | null {
  const normalized = normalizeMerchantName(name);
  if (!normalized) return null;
  return MERCHANT_BRANDS.find((brand) => brand.pattern.test(normalized)) ?? null;
}
